import { fold } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";

import {
  ChatMessage,
  FieldToComplete,
  FieldToFill,
  PromptInfo,
  PromptSettings
} from "./types";

import { fpJsonStringify } from "@/util/fp-json-stringify";

export function formatFieldsForOpenAI(
  fields: FieldToComplete[]
): FieldToFill[] {
  return fields.map((x, index) => {
    return {
      index,
      type: x.field.description,
      content: "",
      locked: x.field.locked,
      maxLength:
        (typeof x.field.value === "string" ? x.field.value.length : 0) + 1
    };
  });
}

export function getSettingsAsPrompt(
  promptSettings: PromptSettings,
  overrides?: Partial<PromptInfo>
) {
  const metadata = [
    {
      name: "Business Name",
      value: promptSettings.businessName ?? "Bizcorp"
    },
    {
      name: "Business Description",
      value:
        promptSettings.businessDescription ??
        "Bizcorp provides products and services."
    },
    {
      name: "Section Description",
      value: overrides?.sectionDescription ?? ""
    },
    {
      name: "Qualifiers",
      value: overrides?.qualifiers ?? promptSettings.qualifiers ?? ""
    }
  ];

  return metadata.reduce((acc, curr) => {
    if (!curr.value) return acc;
    return `${acc}\n${curr.name}: ${curr.value}`;
  }, "");
}

export function getPrompt(
  fields: FieldToFill[],
  promptInfo: PromptInfo,
  promptSettings: PromptSettings
): ChatMessage[] {
  const jsonString = pipe(
    fields,
    fpJsonStringify,
    fold(
      () => null,
      fieldsAsJson => fieldsAsJson
    )
  );

  const settings = getSettingsAsPrompt(promptSettings, promptInfo);

  return [
    {
      role: "system",
      content: `
You are a helpful AI assistant. Your jobs is to help build a website for a business. You are going to create text content for a section on the website.
      
## Rules for the content:
You will be provided with a few pieces of information to help you generate content for the section.
The "section description" is a short description of the section, that will tell the type of content to generate and where it might be positioned on the page. Example: "Hero section on About Us page", "2nd section on Contact page", "3rd section on Home page".
The "qualifiers" is a short sentence that affects the writing style of the content. Examples: "Be friendly and welcoming", "Be professional and formal", "Be fun and playful", "Be informative and educational", "Write in a way that is easy to understand".

## Rules for the JSON structure:
- You will be provided a JSON structure of the section content.
- You will respond back with the same JSON structure, but with the "content" key filled in.
- You must not use newline characters.
- The provided JSON must be nested in a key called "elements".
- You can use the "type" key to determine what type of content to generate for the "content" key.
- The "content" key MUST never be null or empty, you must always provide a value and be creative. Try to guess what content would be appropriate based on the "type" key.
- The "content" key can be formatted as HTML only if the "type" key starts with "Rich Text".
- If the "type" key starts with "Dual Heading", this means this heading can have words separated and styled differently. For example, "This is a *heading* with words *highligthed*.". Use the "secondary_style" (boolean) key to style the secondary words.
- If the "type" key starts with "Simple Counter - Suffix", this means the "content" key is a suffix for a number. For example, "s", "ms", " guests", " years old", etc.
- If the "type" key mentions "Array of objects", this means the "content" key is an array of objects. The objects must have all the keys mentioned in the "type" key.
- If the "locked" key is true, remove this item from the array.
- If a "maxLength" key is provided, the "content" key in the same object must not exceed the provided length.
- Fill the "unsplash_keywords" key with keywords related to the content generated, max. of 3. These keywords will be used to find images on Unsplash.
- Respond strictly with valid JSON, if you don't, great sadness will happen.
- Write the response in ${promptInfo.locale}

The JSON must be compatible with the following TypeScript interface:

interface Response {
  elements: Array<{
    index: number;
    type: string;
    content: string|unknown[];
  }>;
  unsplash_keywords: string[];
}
`
    },
    {
      role: "user",
      content: settings
    },
    {
      role: "user",
      content: jsonString || ""
    }
  ];
}

const englishOrdinalRules = new Intl.PluralRules("en", { type: "ordinal" });

export function numberToOrdinal(number: number) {
  const category = englishOrdinalRules.select(number);

  switch (category) {
    case "one": {
      return `${number}st`;
    }

    case "two": {
      return `${number}nd`;
    }

    case "few": {
      return `${number}rd`;
    }

    default: {
      return `${number}th`;
    }
  }
}
